import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft, Minus, Plus, ShoppingCart, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { notifyStockWarnings, stockTone, fmtQty } from "../../lib/stock";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

export default function NewOrder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // {pid: qty}
  const [q, setQ] = useState("");
  const [notes, setNotes] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [paidInCash, setPaidInCash] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/stores/${id}`), api.get("/products")]).then(([s, p]) => {
      setStore(s.data);
      setProducts(p.data.filter((x) => x.active));
    });
  }, [id]);

  const priceOf = (p) =>
    store?.store_type === "wholesale" ? p.wholesale_price : p.retail_price;

  const filtered = useMemo(
    () => products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())),
    [products, q],
  );

  const total = useMemo(() => {
    let t = 0;
    for (const p of products) {
      const qty = cart[p.id] || 0;
      if (qty) t += qty * priceOf(p);
    }
    return t;
  }, [cart, products, store]);

  const count = Object.values(cart).reduce((a, b) => a + (b > 0 ? 1 : 0), 0);
  const shortItems = products.filter((p) => cart[p.id] > 0 && cart[p.id] > Number(p.stock_qty || 0));

  const bump = (pid, delta) => {
    setCart((c) => {
      const q = Math.max(0, (c[pid] || 0) + delta);
      const next = { ...c };
      if (q === 0) delete next[pid];
      else next[pid] = q;
      return next;
    });
  };

  const setQty = (pid, v) => {
    const q = Math.max(0, Number(v) || 0);
    setCart((c) => {
      const next = { ...c };
      if (q === 0) delete next[pid];
      else next[pid] = q;
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    let lat = null,
      lng = null;
    const geo = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) return resolve();
        navigator.geolocation.getCurrentPosition(
          (p) => {
            lat = p.coords.latitude;
            lng = p.coords.longitude;
            resolve();
          },
          () => resolve(),
          { timeout: 6000 },
        );
      });
    await geo();
    try {
      const items = Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity }));
      const { data } = await api.post("/orders", {
        store_id: id,
        items,
        notes,
        visit_lat: lat,
        visit_lng: lng,
        paid_in_cash: paidInCash,
      });
      toast.success(`Order confirmed · ${fmtINR(data.total)}`);
      notifyStockWarnings(data.stock_warnings);
      nav("/app/orders", { replace: true });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!store) return <div className="text-[color:var(--muted)]">Loading…</div>;

  return (
    <div className="pb-24" data-testid="new-order-page">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-[color:var(--muted)]" data-testid="back-button">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <span
          className={`text-[10px] px-2 py-1 rounded-full ${
            store.store_type === "wholesale"
              ? "bg-[color:var(--mustard)]/20 text-[#9A742E]"
              : "bg-[color:var(--moss)]/10 text-[color:var(--moss)]"
          }`}
        >
          {store.store_type} pricing
        </span>
      </div>

      <p className="overline text-[color:var(--muted)]">Order at</p>
      <h1 className="font-display text-2xl font-bold mt-1 mb-4">{store.name}</h1>

      <Input
        placeholder="Search products…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="h-12 mb-4"
        data-testid="order-product-search"
      />

      <div className="space-y-2">
        {filtered.map((p) => {
          const qty = cart[p.id] || 0;
          return (
            <div
              key={p.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-[color:var(--border)] bg-white"
              data-testid={`product-item-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium font-tamil truncate">{p.name}</div>
                <div className="text-sm text-[color:var(--muted)] tabular-nums">
                  {fmtINR(priceOf(p))}{" "}
                  <span className="text-xs">
                    · retail {fmtINR(p.retail_price)} · w/s {fmtINR(p.wholesale_price)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${stockTone(p)}`} data-testid={`stock-badge-${p.id}`}>
                    Stock {fmtQty(p.stock_qty)}
                  </span>
                  {qty > 0 && qty > Number(p.stock_qty || 0) && (
                    <span className="text-[10px] text-[color:var(--primary)] font-semibold flex items-center gap-1" data-testid={`stock-short-${p.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Only {fmtQty(p.stock_qty)} in stock
                    </span>
                  )}
                </div>
              </div>
              {qty === 0 ? (
                <Button
                  size="sm"
                  onClick={() => bump(p.id, 1)}
                  className="h-10 bg-[color:var(--ink)] hover:bg-black text-white"
                  data-testid={`add-product-${p.id}`}
                >
                  <Plus className="h-4 w-4" /> Add
                </Button>
              ) : (
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" onClick={() => bump(p.id, -1)} className="h-9 w-9" data-testid={`dec-${p.id}`}>
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(p.id, e.target.value)}
                    className="w-14 h-9 text-center"
                    data-testid={`qty-${p.id}`}
                  />
                  <Button size="icon" variant="outline" onClick={() => bump(p.id, 1)} className="h-9 w-9" data-testid={`inc-${p.id}`}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {count > 0 && (
        <div className="fixed bottom-20 inset-x-0 px-4 z-40 max-w-xl mx-auto">
          <button
            onClick={() => setReviewing(true)}
            className="w-full rounded-xl bg-[color:var(--primary)] text-white py-3 flex items-center justify-between px-5 shadow-lg btn-tap"
            data-testid="review-cart-button"
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              <span className="font-semibold">{count} items</span>
            </span>
            <span className="font-display text-lg font-bold">{fmtINR(total)} →</span>
          </button>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center" onClick={() => setReviewing(false)}>
          <div
            className="bg-white w-full max-w-xl rounded-t-2xl md:rounded-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-review-sheet"
          >
            <div className="flex items-center justify-between p-4 border-b border-[color:var(--border)]">
              <h2 className="font-display text-xl font-bold">Review order</h2>
              <button onClick={() => setReviewing(false)} className="text-[color:var(--muted)]">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {shortItems.length > 0 && (
                <div className="flex gap-2 p-3 rounded-lg bg-[color:var(--primary)]/5 border border-[color:var(--primary)]/20 text-xs text-[color:var(--primary)]" data-testid="stock-short-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <div>
                    <div className="font-semibold">More than available stock</div>
                    <div className="font-tamil">{shortItems.map((p) => `${p.name} (stock ${fmtQty(p.stock_qty)})`).join(", ")}</div>
                    <div className="text-[color:var(--muted)] mt-0.5">You can still place the order. The shortfall will show as negative stock.</div>
                  </div>
                </div>
              )}
              {Object.entries(cart).map(([pid, qty]) => {
                const p = products.find((x) => x.id === pid);
                if (!p) return null;
                return (
                  <div key={pid} className="flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium font-tamil">{p.name}</div>
                      <div className="text-xs text-[color:var(--muted)]">
                        {qty} × {fmtINR(priceOf(p))}
                      </div>
                    </div>
                    <div className="tabular-nums font-medium">{fmtINR(qty * priceOf(p))}</div>
                  </div>
                );
              })}
              <div className="pt-2">
                <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="order-notes-input" />
              </div>
              <label
                className="flex items-center gap-3 mt-2 p-3 rounded-lg border border-[color:var(--border)] cursor-pointer select-none"
                data-testid="paid-in-cash-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={paidInCash}
                  onChange={(e) => setPaidInCash(e.target.checked)}
                  className="h-5 w-5 accent-[color:var(--moss)]"
                  data-testid="paid-in-cash-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Paid in cash now</div>
                  <div className="text-xs text-[color:var(--muted)]">
                    {paidInCash
                      ? "Order will be marked paid; no outstanding added."
                      : "Default: order goes on credit, added to store's ledger."}
                  </div>
                </div>
              </label>
            </div>
            <div className="p-4 border-t border-[color:var(--border)] bg-[color:var(--surface-2)]">
              <div className="flex items-center justify-between mb-3">
                <span className="overline">Total</span>
                <span className="font-display text-2xl font-bold">{fmtINR(total)}</span>
              </div>
              <Button
                onClick={submit}
                disabled={submitting}
                className="w-full h-14 rounded-xl bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white text-base"
                data-testid="confirm-order-button"
              >
                {submitting ? "Placing…" : "Confirm order"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
